import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { useApolloClient } from '@apollo/client';

import { InfoRow } from 'common/components/InfoRow';
import {
    IPortfolioItemFragment,
    IPortfolioItemSyncData,
    IPortfolioItemSyncStatus,
    PortfolioItemFragmentDoc,
    useTriggerPortfolioItemSyncMutation,
} from 'graphql/types';
import { getPortfolioItemSyncStatusTransKey } from 'user/utils/portfolioItem';
import { Button } from 'common/components/Button';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { formatDate } from 'common/utils/formatDate';

interface IProps {
    portfolioItemId: string;
    syncData: (IPortfolioItemSyncData | null)[];
}

export const PortfolioItemSyncInfoRows = ({
    portfolioItemId,
    syncData,
}: IProps) => {
    const [translate] = useTranslation();
    const client = useApolloClient();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const [triggerPortfolioItemSync] = useTriggerPortfolioItemSyncMutation();

    // Filter out null values until the type has been fixed to a non null values list
    const syncDataList = syncData.filter(Boolean);

    const handleTriggerPortfolioItemSync = async (
        portfolioItemId: string,
        provider: string,
        index: number
    ) => {
        try {
            const portfolioFragmentVariables = {
                id: `PortfolioItem:${portfolioItemId}`,
                fragment: PortfolioItemFragmentDoc,
                fragmentName: 'PortfolioItem',
            };
            const cachedData = client.readFragment(
                portfolioFragmentVariables
            ) as IPortfolioItemFragment;

            await triggerPortfolioItemSync({
                variables: { portfolioItemId, provider },
                update: (cache) => {
                    // When clicking on the sync button we want the syncdata status of that item
                    // to have an updated status set to busy, to indicate that the sync is in progress
                    cache.updateFragment(
                        portfolioFragmentVariables,
                        (portfolioItemData) => ({
                            ...portfolioItemData,
                            syncData: cachedData.syncData
                                ?.map(
                                    (
                                        syncDataItem: IPortfolioItemSyncData | null,
                                        dataIndex: number
                                    ) => {
                                        if (!syncDataItem) return null;

                                        if (index !== dataIndex) {
                                            return syncDataItem;
                                        }

                                        return {
                                            ...syncDataItem,
                                            status: IPortfolioItemSyncStatus.Busy,
                                        };
                                    }
                                )
                                .filter(Boolean),
                        })
                    );
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('portfolioItemSyncStatus.syncStarted'), {
            variant: 'success',
        });
    };

    return (
        <>
            {syncDataList.map((syncDataItem, index) => {
                const { provider, status, statusLastChanged } = syncDataItem;

                const statusTransKey =
                    getPortfolioItemSyncStatusTransKey(status);

                const isInitial = status === IPortfolioItemSyncStatus.Initial;
                const isSuccess = status === IPortfolioItemSyncStatus.Success;
                const hasFailed = status === IPortfolioItemSyncStatus.Failed;
                const isBusy = status === IPortfolioItemSyncStatus.Busy;

                let translation = translate(statusTransKey);

                if (isSuccess || hasFailed) {
                    translation = translate(statusTransKey, {
                        date: formatDate(
                            statusLastChanged,
                            'd MMMM yyyy, HH:mm'
                        ),
                    });
                } else if (isBusy) {
                    translation = translate('portfolioItemSyncStatus.busy');
                }

                return (
                    <InfoRow
                        description={provider}
                        descriptionSx={{ mt: '3px' }}
                        key={`${provider}-${index}`}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {translation}

                            {(isInitial || hasFailed) && !isBusy && (
                                <Button
                                    size="small"
                                    sx={{ alignSelf: 'flex-start', mt: 1 }}
                                    variant="outlined"
                                    onClick={() =>
                                        handleTriggerPortfolioItemSync(
                                            portfolioItemId,
                                            provider,
                                            index
                                        )
                                    }
                                >
                                    {translate(
                                        isInitial
                                            ? 'portfolioItemSyncStatus.buttonInitial'
                                            : 'portfolioItemSyncStatus.buttonFailed'
                                    )}
                                </Button>
                            )}
                        </Box>
                    </InfoRow>
                );
            })}
        </>
    );
};
